/**
 * JournalEntryCard Component
 * Displays a journal entry in a card format
 */

import {
  Box,
  Text,
  HStack,
  VStack,
  Badge,
  IconButton,
  useColorModeValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Flex,
} from "@chakra-ui/react";
import React from "react";
// Types
import { JournalEntry, JournalEntryType } from "../../../../types/journal";
// Icons
import { AiOutlineEdit, AiOutlineDelete, AiOutlineMore } from "react-icons/ai";

interface JournalEntryCardProps {
  entry: JournalEntry;
  onEdit?: (entry: JournalEntry) => void;
  onDelete?: (entry: JournalEntry) => void;
}

const JournalEntryCard: React.FC<JournalEntryCardProps> = ({
  entry,
  onEdit,
  onDelete,
}) => {
  const bgColor = useColorModeValue("white", "#151517");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("#575757", "white");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const menuBg = useColorModeValue("white", "#1A202C");
  const menuHoverBg = useColorModeValue("gray.50", "gray.700");
  const secondaryTextColor = useColorModeValue("gray.400", "gray.400");
  const labelTextColor = useColorModeValue("gray.500", "gray.400");

  const getTypeColor = (type: JournalEntryType): string => {
    switch (type) {
      case "daily":
        return "blue";
      case "weekly":
        return "purple";
      case "trade_review":
        return "green";
      case "reflection":
        return "orange";
      default:
        return "gray";
    }
  };

  const getTypeLabel = (type: JournalEntryType): string => {
    switch (type) {
      case "daily":
        return "Daily";
      case "weekly":
        return "Weekly";
      case "trade_review":
        return "Trade Review";
      case "reflection":
        return "Reflection";
      default:
        return type;
    }
  };

  const formatDate = (date: Date | string): string => {
    const d = typeof date === "string" ? new Date(date) : date;
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const truncateText = (text: string, maxLength: number = 150): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <Box
      bg={bgColor}
      borderRadius="md"
      p={4}
      _hover={{
        bg: hoverBg,
        transform: "translateY(-2px)",
        boxShadow: "md",
        transition: "all 0.2s",
      }}
      position="relative"
    >
      <Flex justify="space-between" align="start" mb={2}>
        <VStack align="start" spacing={1} flex="1">
          <HStack>
            <Badge colorScheme={getTypeColor(entry.type)} fontSize="xs">
              {getTypeLabel(entry.type)}
            </Badge>
            {entry.moodRating && (
              <Text fontSize="xs" color={secondaryTextColor}>
                Mood: {entry.moodRating}/5
              </Text>
            )}
          </HStack>
          <Text fontSize="lg" fontWeight="bold" color={textColor}>
            {entry.title}
          </Text>
          <Text fontSize="xs" color={secondaryTextColor}>
            {formatDate(entry.date)}
          </Text>
        </VStack>
        <Menu>
          <MenuButton
            as={IconButton}
            icon={<AiOutlineMore />}
            variant="ghost"
            size="sm"
            aria-label="Entry actions"
          />
          <MenuList bg={menuBg} borderColor={borderColor}>
            {onEdit && (
              <MenuItem 
                icon={<AiOutlineEdit />} 
                onClick={() => onEdit(entry)}
                bg={menuBg}
                _hover={{ bg: menuHoverBg }}
                color={textColor}
              >
                Edit
              </MenuItem>
            )}
            {onDelete && (
              <MenuItem
                icon={<AiOutlineDelete />}
                onClick={() => onDelete(entry)}
                bg={menuBg}
                _hover={{ bg: menuHoverBg }}
                css={{
                  background: "linear-gradient(135deg, #E00101, #FE4244)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  color: "transparent",
                }}
              >
                Delete
              </MenuItem>
            )}
          </MenuList>
        </Menu>
      </Flex>

      <Text fontSize="sm" color={textColor} mb={3} noOfLines={4}>
        {truncateText(entry.content)}
      </Text>

      {entry.lessonsLearned && entry.lessonsLearned.length > 0 && (
        <Box mb={2}>
          <Text fontSize="xs" fontWeight="medium" color={labelTextColor} mb={1}>
            Key Takeaways:
          </Text>
          <VStack align="start" spacing={1}>
            {entry.lessonsLearned.slice(0, 2).map((lesson, index) => (
              <Text key={index} fontSize="xs" color={secondaryTextColor}>
                • {truncateText(lesson, 60)}
              </Text>
            ))}
          </VStack>
        </Box>
      )}

      {entry.tags && entry.tags.length > 0 && (
        <HStack spacing={1} flexWrap="wrap" mt={2}>
          {entry.tags.slice(0, 3).map((tag) => (
            <Badge key={tag} colorScheme="teal" fontSize="xs">
              {tag}
            </Badge>
          ))}
          {entry.tags.length > 3 && (
            <Text fontSize="xs" color={secondaryTextColor}>
              +{entry.tags.length - 3} more
            </Text>
          )}
        </HStack>
      )}

      {entry.relatedTradeIds && entry.relatedTradeIds.length > 0 && (
        <Text fontSize="xs" color={secondaryTextColor} mt={2}>
          Related to {entry.relatedTradeIds.length} trade(s)
        </Text>
      )}
    </Box>
  );
};

export default JournalEntryCard;

